'use client';

import { useEffect, useRef, useState } from 'react';
import { MoreHorizontal, VolumeX } from 'lucide-react';
import { Avatar } from '@/components/ds';
import { cn } from '@/lib/utils';
import {
  type GroupRole,
  canAssignRole,
  canBan,
  canModerateTarget,
} from '@/lib/groups/authority';
import RoleBadge from './RoleBadge';

export type RosterMember = {
  userId: string;
  name: string;
  username: string | null;
  avatarUrl: string | null;
  role: GroupRole | null;
  mutedUntil: string | null;
};

type Props = {
  members: RosterMember[];
  viewerId: string;
  viewerRole: GroupRole | null;
  ownerId: string;
  onlineIds: Set<string>;
  onChangeRole: (userId: string, role: 'admin' | 'moderator' | 'member') => Promise<void>;
  onMute: (userId: string, minutes: number | null) => Promise<void>;
  onKick: (userId: string) => Promise<void>;
  onBan: (userId: string) => Promise<void>;
};

// "Until I unmute" — the mute endpoint clears the mute on null/<=0, so an
// indefinite mute must be expressed as a far-future timestamp via a large
// minutes value (null is reserved for the explicit Unmute action).
const INDEFINITE_MUTE_MINUTES = 60 * 24 * 365 * 100;

const ROLE_CANDIDATES: Array<'admin' | 'moderator' | 'member'> = [
  'admin',
  'moderator',
  'member',
];

function displayRole(m: RosterMember, ownerId: string): GroupRole {
  if (m.userId === ownerId) return 'owner';
  return m.role ?? 'member';
}

function isMutedNow(mutedUntil: string | null): boolean {
  if (!mutedUntil) return false;
  const t = new Date(mutedUntil).getTime();
  return Number.isFinite(t) && t > Date.now();
}

export default function RosterRail({
  members,
  viewerId,
  viewerRole,
  ownerId,
  onlineIds,
  onChangeRole,
  onMute,
  onKick,
  onBan,
}: Props) {
  const [openMenuFor, setOpenMenuFor] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const menuWrapRef = useRef<HTMLDivElement>(null);

  // Close the open menu on any outside click.
  useEffect(() => {
    if (!openMenuFor) return;
    const onDown = (e: MouseEvent) => {
      if (menuWrapRef.current && !menuWrapRef.current.contains(e.target as Node)) {
        setOpenMenuFor(null);
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [openMenuFor]);

  const online = members.filter((m) => onlineIds.has(m.userId));
  // Presence is not wired yet — only the viewer is known-online. Everyone else
  // falls into Offline until a presence signal exists. Structure is ready for it.
  const offline = members.filter((m) => !onlineIds.has(m.userId));

  const runAction = async (userId: string, p: Promise<void>) => {
    setBusyId(userId);
    setActionError(null);
    try {
      await p;
      setOpenMenuFor(null);
    } catch {
      setActionError('Action failed. Please try again.');
    } finally {
      setBusyId(null);
    }
  };

  const renderRow = (m: RosterMember, dimmed: boolean) => {
    const role = displayRole(m, ownerId);
    const isSelf = m.userId === viewerId;
    const isOwnerRow = m.userId === ownerId;
    const muted = isMutedNow(m.mutedUntil);

    const roleOptions = ROLE_CANDIDATES.filter((c) =>
      canAssignRole(viewerRole, role, c),
    );
    const mayModerate = canModerateTarget(viewerRole, role);
    const mayBan = canBan(viewerRole, role);
    // Never offer actions on the owner row or the viewer's own row.
    const showMenu =
      !isOwnerRow &&
      !isSelf &&
      (roleOptions.length > 0 || mayModerate || mayBan);

    const menuOpen = openMenuFor === m.userId;

    return (
      <li
        key={m.userId}
        className={cn(
          'group/row relative flex items-center gap-2 px-3 py-1.5 hover:bg-surface-2',
          dimmed && 'opacity-60',
        )}
      >
        <Avatar
          src={m.avatarUrl || undefined}
          name={m.name}
          size={28}
          username={m.username || null}
          online={onlineIds.has(m.userId)}
        />
        <div className="min-w-0 flex-1">
          <p className="text-sm text-text-primary truncate flex items-center gap-1.5">
            {m.name}
            {muted && (
              <VolumeX
                className="w-3.5 h-3.5 text-text-tertiary flex-shrink-0"
                strokeWidth={1.75}
                aria-label="Muted"
              />
            )}
          </p>
        </div>
        <RoleBadge role={role} />

        {showMenu && (
          <div ref={menuOpen ? menuWrapRef : undefined} className="relative flex-shrink-0">
            <button
              type="button"
              aria-label={`Moderate ${m.name}`}
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              disabled={busyId === m.userId}
              onClick={() => setOpenMenuFor(menuOpen ? null : m.userId)}
              className={cn(
                'w-7 h-7 inline-flex items-center justify-center rounded-sm transition-colors',
                'text-text-tertiary hover:text-text-primary hover:bg-surface-3',
                'opacity-0 group-hover/row:opacity-100 focus-visible:opacity-100',
                menuOpen && 'opacity-100 bg-surface-3 text-text-primary',
                busyId === m.userId && 'opacity-100',
              )}
            >
              <MoreHorizontal className="w-4 h-4" strokeWidth={1.75} />
            </button>

            {menuOpen && (
              <div
                role="menu"
                className="absolute right-0 top-full mt-1 z-50 w-48 py-1 rounded-md bg-surface-2 border border-border-default shadow-lg"
              >
                {roleOptions.length > 0 && (
                  <>
                    <p className="px-3 pt-1.5 pb-1 text-[10px] uppercase tracking-wider font-mono text-text-tertiary">
                      Change role
                    </p>
                    {roleOptions.map((c) => (
                      <MenuItem
                        key={c}
                        disabled={busyId === m.userId || role === c}
                        onClick={() => runAction(m.userId, onChangeRole(m.userId, c))}
                      >
                        {c === 'admin' ? 'Admin' : c === 'moderator' ? 'Moderator' : 'Member'}
                        {role === c && <span className="ml-auto text-text-tertiary">current</span>}
                      </MenuItem>
                    ))}
                  </>
                )}

                {mayModerate && (
                  <>
                    <p className="px-3 pt-1.5 pb-1 text-[10px] uppercase tracking-wider font-mono text-text-tertiary">
                      Mute in chat
                    </p>
                    {muted && (
                      <MenuItem
                        disabled={busyId === m.userId}
                        onClick={() => runAction(m.userId, onMute(m.userId, null))}
                      >
                        Unmute
                      </MenuItem>
                    )}
                    <MenuItem disabled={busyId === m.userId} onClick={() => runAction(m.userId, onMute(m.userId, 10))}>
                      10 minutes
                    </MenuItem>
                    <MenuItem disabled={busyId === m.userId} onClick={() => runAction(m.userId, onMute(m.userId, 60))}>
                      1 hour
                    </MenuItem>
                    <MenuItem disabled={busyId === m.userId} onClick={() => runAction(m.userId, onMute(m.userId, 1440))}>
                      1 day
                    </MenuItem>
                    <MenuItem
                      disabled={busyId === m.userId}
                      onClick={() => runAction(m.userId, onMute(m.userId, INDEFINITE_MUTE_MINUTES))}
                    >
                      Until I unmute
                    </MenuItem>
                  </>
                )}

                {(mayModerate || mayBan) && (
                  <div className="my-1 border-t border-border-subtle" />
                )}
                {mayModerate && (
                  <MenuItem
                    danger
                    disabled={busyId === m.userId}
                    onClick={() => runAction(m.userId, onKick(m.userId))}
                  >
                    Kick from group
                  </MenuItem>
                )}
                {mayBan && (
                  <MenuItem
                    danger
                    disabled={busyId === m.userId}
                    onClick={() => runAction(m.userId, onBan(m.userId))}
                  >
                    Ban member
                  </MenuItem>
                )}
              </div>
            )}
          </div>
        )}
      </li>
    );
  };

  return (
    <div className="h-full flex flex-col bg-surface-1 border-l border-border-subtle">
      <div className="px-4 pt-3 pb-2 flex items-center justify-between">
        <span className="text-[11px] uppercase tracking-wider font-mono text-text-tertiary">
          Members — {members.length}
        </span>
      </div>

      {actionError && (
        <div
          role="alert"
          aria-live="polite"
          className="mx-3 mb-2 px-2.5 py-1.5 rounded-sm text-[11px] bg-status-busy/10 border border-status-busy/30 text-status-busy"
        >
          {actionError}
        </div>
      )}

      <div className="flex-1 overflow-y-auto pb-2">
        {online.length > 0 && (
          <>
            <p className="px-4 pt-1 pb-1 text-[10px] uppercase tracking-wider font-mono text-text-tertiary">
              Online — {online.length}
            </p>
            <ul>{online.map((m) => renderRow(m, false))}</ul>
          </>
        )}
        {offline.length > 0 && (
          <>
            <p className="px-4 pt-2 pb-1 text-[10px] uppercase tracking-wider font-mono text-text-tertiary">
              Offline — {offline.length}
            </p>
            <ul>{offline.map((m) => renderRow(m, true))}</ul>
          </>
        )}
        {members.length === 0 && (
          <p className="px-3 py-4 text-xs text-text-tertiary">No members yet.</p>
        )}
      </div>
    </div>
  );
}

function MenuItem({
  children,
  onClick,
  disabled,
  danger,
}: {
  children: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  danger?: boolean;
}) {
  return (
    <button
      type="button"
      role="menuitem"
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'w-full flex items-center gap-2 px-3 py-1.5 text-left text-sm transition-colors',
        danger
          ? 'text-status-busy hover:bg-status-busy/10'
          : 'text-text-secondary hover:bg-surface-3 hover:text-text-primary',
        disabled && 'opacity-40 cursor-not-allowed hover:bg-transparent',
      )}
    >
      {children}
    </button>
  );
}
